<template>
  <div>
    <a-skeleton active :loading="policy_info_loading">
      <a-row
        type="flex"
        justify="space-between"
        align="middle"
        class="mt-3 mb-2"
      >
        <a-row type="flex" align="middle">
          <svg
            focusable="false"
            data-icon="arrow-left"
            width="1.3em"
            height="2em"
            fill="currentColor"
            aria-hidden="true"
            viewBox="64 64 896 896"
            style="cursor: pointer"
            @click="goBack"
          >
            <path
              d="M872 474H286.9l350.2-304c5.6-4.9 2.2-14-5.2-14h-88.5c-3.9 0-7.6 1.4-10.5 3.9L155 487.8a31.96 31.96 0 000 48.3L535.1 866c1.5 1.3 3.3 2 5.2 2h91.5c7.4 0 10.8-9.2 5.2-14L286.9 550H872c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8z"
            ></path>
          </svg>
          <a-typography-title :level="4" class="ml-1" style="margin-bottom: 0">
            Póliza Nº {{ policy_data.numberPolicy }}
          </a-typography-title>
        </a-row>
        <a-button @click="openEditPolicy">Editar info</a-button>
      </a-row>
      <a-descriptions bordered>
        <a-descriptions-item label="Número póliza" :span="3">
          {{ policy_data.numberPolicy }}
        </a-descriptions-item>
        <a-descriptions-item label="Compañía aseguradora" :span="3">
          {{ policy_data.insuranceCompany }}
        </a-descriptions-item>
        <a-descriptions-item label="Corredora" :span="3">
          {{ policy_data.insuranceBroker }}
        </a-descriptions-item>
        <a-descriptions-item label="Grupo" :span="3">
          {{ policy_data.groupCompanies }}
        </a-descriptions-item>
        <a-descriptions-item label="Fecha vigencia" :span="3">
          {{ policy_data.initCurrency }}
        </a-descriptions-item>
        <a-descriptions-item label="Fecha término" :span="3">
          {{ policy_data.endCurrency }}
        </a-descriptions-item>
        <a-descriptions-item label="Documentos" :span="3">
          <ul v-if="policy_data?.docs?.length > 0">
            <li v-for="document in policy_data.docs" :key="document.id">
              <a
                v-if="/\.pdf$/i.test(document.fileName)"
                @click="openPdfViewerModal(document.filePolicyURL)"
              >
                {{ document.fileName }}
              </a>
              <a
                v-else
                :href="document.filePolicyURL"
                target="_blank"
                rel="noreferrer noopener"
              >
                {{ document.fileName }}
              </a>
            </li>
          </ul>
          <p v-else>Sin documentos</p>
        </a-descriptions-item>
      </a-descriptions>

      <a-row type="flex" justify="space-between" class="mt-4">
        <a-typography-title :level="4">Coberturas</a-typography-title>
        <a-button @click="openAddCoverageModal">Agregar coberturas</a-button>
      </a-row>
    </a-skeleton>

    <PolicyCoverages
      class="mb-3"
      :data="policy_coverages"
      @openEditCoverage="openEditCoverage"
      @openDeleteCoverage="openDeleteCoverage"
    />

    <div
      class="coverage-box"
      v-if="
        company_groups_coverages ? company_groups_coverages.length === 0 : true
      "
    >
      <p>No hay coberturas</p>
    </div>
    <div v-else>
      <div v-for="comp_group in company_groups_coverages" :key="comp_group.id">
        <CompanyGroupCoverages
          class="mb-3"
          :data="comp_group"
          :groupName="comp_group.name"
          @primeConfiguration="openPrimeConfiguration"
        />
      </div>
    </div>

    <PDFViewer ref="pdf_viewer" />
    <PolicyEditModal ref="edit_policy_ref" @editedPolicy="getMainData" />
    <AddCoverage
      ref="add_coverage_modal"
      :groups="company_group"
      @coverageCreated="coverageCreated"
    />
    <EditCoverage ref="edit_coverage_ref" :groups="company_group" />
    <DeleteCoverage ref="delete_coverage_ref" :groups="company_group" />
    <PrimeConfiguration
      ref="prime_configuration"
      @completed="completedPrimeConfiguration"
    />
  </div>
</template>

<script setup>
import { notification } from 'ant-design-vue';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  FETCH_POLICY,
  FETCH_POLICY_COVERAGES,
  FETCH_POLICY_GROUP_COMPANY,
  FETCH_GROUP_COMPANY_PREMIUM_CALC,
  COVERAGE_GROUP_COMPANIES_FETCH_COVERAGE_GROUP_COMPANIES
} from '@/store/types';
import PDFViewer from '@/components/pdf-viewer';
import PolicyEditModal from '@/components/clients/policy/policy-edit-modal';
import AddCoverage from '@/components/policy/add-coverage';
import CompanyGroupCoverages from '@/components/policy/company-group-coverages';
import EditCoverage from '@/components/policy/edit-coverage';
import DeleteCoverage from '@/components/policy/delete-coverage';
import PolicyCoverages from '@/components/clients/policy/policy-coverages';
import PrimeConfiguration from '@/components/clients/policy/prime-configuration.vue';
import EventBus from '@/utils/events';

const store = useStore();
const router = useRouter();
const policy_data = ref({});
const route = useRoute();
const {
  // id:company_id,
  policyId
} = route.params;
const policy_object = ref({});
const policy_info_loading = ref(false);
const pdf_viewer = ref();
const edit_policy_ref = ref();
const policy_coverages = ref([]);
const company_groups_coverages = ref([]);
const company_group = ref([]);
const add_coverage_modal = ref();
const edit_coverage_ref = ref();
const delete_coverage_ref = ref();
const prime_configuration = ref();
const policy_coverages_loading = ref(false);

const openEditPolicy = () => {
  edit_policy_ref.value.openModal(policy_object.value);
};

const openPdfViewerModal = (url) => {
  pdf_viewer.value.openModal(url);
};

const getPolicy = async () => {
  policy_info_loading.value = true;
  try {
    const resp = await store.dispatch(FETCH_POLICY, policyId);
    policy_object.value = resp;
    const {
      numberPolicy,
      insuranceCompany,
      insuranceBroker,
      groupCompanies,
      startDate,
      endDate,
      filePolicies
    } = resp;

    policy_data.value = {
      numberPolicy,
      insuranceCompany: insuranceCompany.businessName,
      insuranceBroker: insuranceBroker?.label ?? null,
      groupCompanies:
        groupCompanies?.length > 0
          ? groupCompanies.map((group) => group.name).join(', ')
          : ' - ',
      initCurrency: startDate,
      endCurrency: endDate,
      docs: filePolicies
    };

    company_group.value = groupCompanies.map((group) => ({
      id: group.id,
      name: group.name
    }));
  } catch (error) {
    notification('error', 'Error', error.response.data.message);
    router.push({ name: 'Missing' });
  }
  policy_info_loading.value = false;
};

const getPolicyCoverages = async () => {
  policy_coverages_loading.value = true;
  try {
    const resp = await store.dispatch(FETCH_POLICY_COVERAGES, policyId);
    policy_coverages.value = resp;
  } catch (error) {
    notification('error', 'Error', error.response.data.message);
  }
  policy_coverages_loading.value = false;
};

const getPolicyCompanyGroups = async () => {
  try {
    const resp = await store.dispatch(FETCH_POLICY_GROUP_COMPANY, policyId);
    company_groups_coverages.value = [];

    for (const group of resp) {
      const coverages = await getCompanyGroupCoverages(group.id);
      const coverageGroupCompanies = await store.dispatch(
        COVERAGE_GROUP_COMPANIES_FETCH_COVERAGE_GROUP_COMPANIES,
        {
          policyId: Number(policyId),
          groupCompanyId: group.id
        }
      );

      if (coverages != null) {
        const coverageGroupCompanyIdByCoverageId = {};
        coverageGroupCompanies.forEach((coverageGroupCompany) => {
          const coverageId = Number(coverageGroupCompany?.coverage?.id);
          if (!Number.isNaN(coverageId)) {
            coverageGroupCompanyIdByCoverageId[coverageId] =
              coverageGroupCompany.id;
          }
        });

        company_groups_coverages.value.push({
          groupCompany: group.id,
          ...coverages,
          coverages: (coverages.coverages || []).map((coverage) => ({
            ...coverage,
            coverageGroupCompanyId:
              coverageGroupCompanyIdByCoverageId[Number(coverage.id)]
          }))
        });
      }
    }
  } catch (error) {
    notification('error', 'Error', error.response.data.message);
  }
};

const getCompanyGroupCoverages = async (groupId) => {
  try {
    const resp = await store.dispatch(FETCH_GROUP_COMPANY_PREMIUM_CALC, {
      policyId,
      groupId
    });

    return resp.data;
  } catch (error) {
    notification('error', 'Error', error.response.data.message);
  }
};

const openAddCoverageModal = () => {
  add_coverage_modal.value.openModal();
};

const coverageCreated = () => {
  getPolicyCoverages();
  getPolicyCompanyGroups();
};

// const getCompanyGroupName = (id) => {
//   const name = company_group.value.find(obj => obj.id === id).name;
//   console.log('name',name);
//   return name;
// };

const openEditCoverage = (coverageId) => {
  edit_coverage_ref.value.openModal(coverageId);
};

const openDeleteCoverage = (coverageId) => {
  delete_coverage_ref.value.openModal(coverageId);
};

const openPrimeConfiguration = (value) => {
  prime_configuration.value.openModal(value);
};

const completedPrimeConfiguration = () => {
  getMainData();
};

const goBack = () => {
  router.go(-1);
};

const getMainData = () => {
  getPolicy();
  getPolicyCoverages();
  getPolicyCompanyGroups();
};

onMounted(() => {
  getMainData();
});

EventBus.on('coverageEdited', () => {
  getPolicyCoverages();
  getPolicyCompanyGroups();
});

EventBus.on('coverageDeleted', () => {
  getPolicyCoverages();
  getPolicyCompanyGroups();
});
</script>
